import { ExternalContextSource, MonacoLanguageType } from "@hex/common";
import { z } from "zod";
import { EmptyObject } from "type-fest";

import { ContextWorkbenchEntityRef } from "./contextWorkbenchEntity";
import { WorkbenchDiffStatus } from "../../workbench/state/diffStatus";

type AnyObject = Record<string, unknown>;

export const ContextWorkbenchFileId = z
  .string()
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- building a schema is pure; the rule cannot see through a chained builder
  .brand("ContextWorkbenchFileId");

export type ContextWorkbenchFileId = z.infer<typeof ContextWorkbenchFileId>;

/** A file in the context workbench. */
export type ContextWorkbenchFile = ContextWorkbenchFile.Entity;

/** A file in the context workbench. */
export namespace ContextWorkbenchFile {
  export type Type = Type.Entity;

  export namespace Type {
    export const Entity = "Entity" as const;
    export type Entity = typeof Type.Entity;
  }

  /**
   * Common properties for any file displayed in the context workbench,
   * independent of its backing data source.
   */
  type _Base<
    TType extends Type,
    TProperties extends AnyObject = EmptyObject,
  > = {
    id: ContextWorkbenchFileId;
    type: TType;
    readonly contents: string | null;
    readonly filepath: string;
    readonly stem: string;
    readonly extension: string;
    readonly language: MonacoLanguageType;
    readonly sizeBytes: number | null;
    readonly readOnly: boolean;
    readonly canDelete: boolean;
  } & TProperties;

  /**
   * A file that points to an entity. The file maintains a stable ID for tab
   * persistence while the underlying entity may be loading or absent.
   */
  export type Entity = _Base<
    "Entity",
    {
      entityRef: ContextWorkbenchEntityRef;
    }
  >;

  /**
   * A file enriched with its diff status/base contents
   * (and, for guides, the originating external source)
   */
  export type EntityWithDiff = ContextWorkbenchFile.Entity & {
    diff: {
      status: WorkbenchDiffStatus;
      baseContents: string | null;
    };
    externalSource: ExternalContextSource | null;
  };
}
